'use-client';
import * as React from 'react';
import cn from 'clsx';
import { formatClassNames } from '../../../../../utils/classNames';
import { getDataAttributes } from '../../../../../utils/dataUtils';
import type { MenuItemProps } from '@wix/editor-elements-definitions';
import type { IMenuItemSDKAction } from '../../../../../common/sdk/props-factories/menuItemsPropsSDKFactory';
import type { FC } from 'react';
import type { SubmenuPreviewMapperProps, SubmenuProps } from './types';
import { useMenuItemContext } from '../../contexts/MenuItemContext';
import { isCurrentItem } from '../../menuItems';
import styles from './styles/Submenu.module.scss';
import menuItemStyles from '../MenuContent/MenuItem/style/MenuItem.module.scss';
import Link from '../../../../../common/Link/Link';
import { TestIds, DataHooks, INNER_FOCUS_RING_CLASSNAME } from './constants';
import {
  getItemDepthSelector,
  subItemDepthLevel,
  subSubItemDepthLevel,
} from '../../utils';
import { dropdownSemanticClassNames } from '../../semanticClassNames';
import { MenuParts, selectors } from '../../constants';
import { createSDKAction } from '../MenuContent/MenuItem/utils';

type ItemListProps = SubmenuPreviewMapperProps & {
  items: Array<MenuItemProps>;
  currentItem?: MenuItemProps;
  isRootLevel?: boolean;
  onEscKeyDown?: () => void;
  customClassNames?: Array<string>;
  onItemClick?: IMenuItemSDKAction;
  onItemMouseIn?: IMenuItemSDKAction;
  onItemMouseOut?: IMenuItemSDKAction;
  onItemDblClick?: IMenuItemSDKAction;
};

const ItemList: FC<ItemListProps> = ({
  items,
  currentItem,
  isRootLevel = true,
  partToPreviewStateMap,
  onEscKeyDown,
  customClassNames = [],
  onItemClick,
  onItemMouseIn,
  onItemMouseOut,
  onItemDblClick,
}) => {
  const subMenuContainerClasses = cn(
    isRootLevel ? styles.list : styles.subList,
    menuItemStyles.dropdownMenu,
    {
      [formatClassNames(dropdownSemanticClassNames.root, ...customClassNames)]:
        isRootLevel,
    },
  );

  const submenuItemLinkClasses = (isCurrent: boolean) => {
    return cn(
      isRootLevel
        ? [styles.item, selectors.dropdownItem]
        : [styles.subItem, selectors.dropdownSubItem],
      {
        [styles.selected]: isCurrent,
      },
      isRootLevel
        ? {
            [formatClassNames(dropdownSemanticClassNames.dropdownItem)]: true,
          }
        : {
            [formatClassNames(dropdownSemanticClassNames.dropdownSubItem)]:
              true,
          },
      INNER_FOCUS_RING_CLASSNAME,
    );
  };

  const submenuItemLabelClasses = cn(
    isRootLevel ? styles.itemLabel : styles.subItemLabel,
    isRootLevel
      ? formatClassNames(dropdownSemanticClassNames.dropdownItemLabel)
      : formatClassNames(dropdownSemanticClassNames.dropdownSubItemLabel),
  );

  return (
    <ul
      className={subMenuContainerClasses}
      style={
        {
          '--items-number': items.length,
        } as React.CSSProperties
      }
      onKeyDown={e => {
        if (e.key === 'Escape') {
          if (onEscKeyDown) {
            onEscKeyDown();

            return;
          }

          const target = e.target as HTMLElement;
          const parentListItem = target.closest('li') as HTMLElement;

          const parentNavigateTo = parentListItem.closest(
            getItemDepthSelector(0),
          );

          (parentNavigateTo?.firstChild as HTMLElement)?.focus();
        }
      }}
    >
      {items.map((item, i) => {
        const { items: subItems, link, label } = item;
        const isCurrent = isCurrentItem(item, currentItem);

        return (
          <li
            className={cn({ [styles.listItem]: !isRootLevel })}
            key={i}
            data-item-depth={
              isRootLevel ? subItemDepthLevel : subSubItemDepthLevel
            }
          >
            <Link
              {...link}
              className={submenuItemLinkClasses(isCurrent)}
              dataPart={
                isRootLevel ? MenuParts.DropdownItem : MenuParts.DropdownSubItem
              }
              dataPreview={
                partToPreviewStateMap?.[isRootLevel ? 'item' : 'sub-item']
              }
              onFocus={e => {
                e.stopPropagation();
              }}
              onClick={createSDKAction(item, isCurrent, onItemClick)}
              onMouseEnter={createSDKAction(item, isCurrent, onItemMouseIn)}
              onMouseLeave={createSDKAction(item, isCurrent, onItemMouseOut)}
              onDoubleClick={createSDKAction(item, isCurrent, onItemDblClick)}
              {...(isCurrent && {
                'aria-current': 'page',
              })}
            >
              <span
                className={submenuItemLabelClasses}
                data-selected={isCurrent ? 'true' : undefined}
                data-part={
                  isRootLevel
                    ? MenuParts.DropdownItemLabel
                    : MenuParts.DropdownSubItemLabel
                }
                data-testid={
                  isRootLevel ? TestIds.itemLabel : TestIds.subItemLabel
                }
              >
                {label}
              </span>
            </Link>

            {!!subItems?.length && (
              <ItemList
                partToPreviewStateMap={partToPreviewStateMap}
                items={subItems}
                isRootLevel={false}
                currentItem={currentItem}
                onItemClick={onItemClick}
                onItemMouseIn={onItemMouseIn}
                onItemMouseOut={onItemMouseOut}
                onItemDblClick={onItemDblClick}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
};

const Submenu: React.FC<SubmenuProps> = props => {
  const {
    id,
    partToPreviewStateMap,
    customClassNames = [],
    className,
    onItemClick,
    onItemMouseIn,
    onItemMouseOut,
    onItemDblClick,
  } = props;
  const { item, currentItem, onEscKeyDown } = useMenuItemContext();

  return (
    <div
      id={id}
      {...getDataAttributes(props)}
      className={cn(className, styles.root)}
      data-hook={DataHooks.submenu}
    >
      <ItemList
        partToPreviewStateMap={partToPreviewStateMap}
        items={item.items || []}
        currentItem={currentItem}
        onEscKeyDown={onEscKeyDown}
        customClassNames={customClassNames}
        onItemClick={onItemClick}
        onItemMouseIn={onItemMouseIn}
        onItemMouseOut={onItemMouseOut}
        onItemDblClick={onItemDblClick}
      />
    </div>
  );
};

export default Submenu;
